import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, Mail, Users, History } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const fr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const MemberRow = ({ m, checked, onToggle }) => (
  <label className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${m.can_email ? 'border-gray-100 hover:bg-gray-50 cursor-pointer' : 'border-dashed border-gray-200 opacity-60'}`}
    data-testid={`renewal-member-${m.id}`}>
    <Checkbox checked={checked} disabled={!m.can_email} onCheckedChange={onToggle} data-testid={`renewal-check-${m.id}`} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
      <p className="text-xs text-gray-500 truncate">{m.can_email ? m.email : 'Pas d\'adresse email valide'}</p>
    </div>
    <div className="text-right shrink-0">
      <p className={`text-xs font-semibold ${m.days_left <= 7 ? 'text-rose-600' : 'text-amber-600'}`}>
        {m.days_left > 0 ? `Masquée le ${fr(m.hidden_on)}` : 'Masquée au prochain contrôle'}
      </p>
      <p className="text-[11px] text-gray-400">
        Expirée le {fr(m.expired_on)}{m.reminders_count > 0 && ` · relancé ${m.reminders_count}× (${fr(m.last_reminder_sent)})`}
      </p>
    </div>
  </label>
);

const CampaignHistory = ({ history }) => history.length > 0 && (
  <div className="border-t border-gray-100 pt-3" data-testid="renewal-history">
    <p className="text-xs font-bold text-gray-500 flex items-center gap-1.5 mb-2"><History size={13} /> Dernières campagnes</p>
    {history.map(c => (
      <p key={c.id} className="text-xs text-gray-600 py-0.5" data-testid={`renewal-history-${c.id}`}>
        {fr(c.created_at)} · {c.created_by_name || 'Admin'} · {c.total} membre(s) —{' '}
        {c.status === 'sending' ? 'envoi en cours…' : `${c.sent} envoyé(s)${c.failed ? `, ${c.failed} en échec` : ''}${c.skipped ? `, ${c.skipped} non envoyé(s) (envoi désactivé ou adresse invalide)` : ''}`}
      </p>
    ))}
  </div>
);

export const RenewalCampaignDialog = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);

  const load = () => axios.get(`${API}/admin/renewal-campaign/preview`).then(r => {
    setData(r.data);
    setSelected(new Set(r.data.members.filter(m => m.can_email).map(m => m.id)));
  }).catch(() => toast.error('Impossible de charger la liste des membres à relancer'));

  useEffect(() => { if (open) { setData(null); load(); } }, [open]);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const emailable = data?.members.filter(m => m.can_email) || [];
  const allChecked = emailable.length > 0 && emailable.every(m => selected.has(m.id));

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/renewal-campaign/send`, { member_ids: [...selected] });
      toast.success(r.data.message);
      setSelected(new Set());
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi de la campagne");
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="renewal-campaign-dialog">
        <DialogHeader>
          <DialogTitle>Relancer avant masquage</DialogTitle>
          <DialogDescription>Praticiens dont l'adhésion a expiré mais dont la fiche est encore visible dans l'annuaire (mois de grâce).</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#35AFE4]" /></div>
        ) : (
          <div className="space-y-4">
            <Tabs defaultValue="members">
              <TabsList>
                <TabsTrigger value="members" data-testid="renewal-tab-members"><Users size={13} className="mr-1.5" />Destinataires ({selected.size})</TabsTrigger>
                <TabsTrigger value="email" data-testid="renewal-tab-email"><Mail size={13} className="mr-1.5" />Aperçu de l'email</TabsTrigger>
              </TabsList>
              <TabsContent value="members" className="space-y-2 mt-3">
                {data.members.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8" data-testid="renewal-empty">Aucune fiche en sursis : tous les praticiens visibles sont à jour.</p>
                ) : (
                  <>
                    <label className="flex items-center gap-2 text-xs text-gray-600 px-1 cursor-pointer">
                      <Checkbox checked={allChecked} onCheckedChange={v => setSelected(new Set(v ? emailable.map(m => m.id) : []))} data-testid="renewal-check-all" />
                      Tout sélectionner
                    </label>
                    {data.members.map(m => <MemberRow key={m.id} m={m} checked={selected.has(m.id)} onToggle={() => toggle(m.id)} />)}
                  </>
                )}
              </TabsContent>
              <TabsContent value="email" className="mt-3" data-testid="renewal-email-preview">
                <p className="text-xs text-gray-500 mb-1">Objet : <strong className="text-gray-800">{data.subject}</strong></p>
                <p className="text-[11px] text-gray-400 mb-2">Exemple pour {data.sample_name} — chaque email est personnalisé (nom, dates). Le bouton mène à la page Adhésion de l'application.</p>
                <iframe title="Aperçu de l'email" srcDoc={data.sample_html} className="w-full h-[420px] rounded-lg border border-gray-100" sandbox="" />
              </TabsContent>
            </Tabs>
            <div className="flex justify-end gap-2">
              <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50" data-testid="renewal-cancel-btn">Fermer</button>
              <button onClick={send} disabled={sending || selected.size === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
                data-testid="renewal-send-btn">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Envoyer à {selected.size} membre(s)
              </button>
            </div>
            <CampaignHistory history={data.history} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
